/**
 * Goals Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goalsApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';
import { StrategicGoalList, StrategicGoal } from '../../types';

interface GoalsListParams {
  page?: number;
  pageSize?: number;
  department_id?: number;
  year?: number;
  status?: string;
}

interface GoalsListResponse {
  results: StrategicGoalList[];
  count: number;
}

export function useGoalsQuery(params?: GoalsListParams) {
  return useQuery({
    queryKey: queryKeys.goals.list(params),
    queryFn: async (): Promise<GoalsListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.department_id) queryParams.department_id = params.department_id;
      if (params?.year) queryParams.year = params.year;
      if (params?.status) queryParams.status = params.status;

      return await goalsApi.list(queryParams);
    },
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useGoalDetailQuery(goalId: number) {
  return useQuery({
    queryKey: queryKeys.goals.detail(goalId),
    queryFn: () => goalsApi.get(goalId),
    enabled: !!goalId,
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useGoalKPIsQuery(goalId: number) {
  return useQuery({
    queryKey: queryKeys.goals.kpis(goalId),
    queryFn: () => goalsApi.getKPIs(goalId),
    enabled: !!goalId,
    staleTime: STALE_TIMES.KPIS,
  });
}

export function useDeleteGoalMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId: number) => goalsApi.delete(goalId),
    onSuccess: () => {
      // Invalidate goals list
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
    },
  });
}

export function useCreateGoalMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<StrategicGoal>) => goalsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
    },
  });
}

export function useUpdateGoalMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<StrategicGoal> }) =>
      goalsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.detail(variables.id) });
    },
  });
}

export default useGoalsQuery;
